import type { ChangeEvent, CompositionEvent } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'

export interface UseCompositionProps<T> {
    value?: T
    onChange?: (ev: ChangeEvent<HTMLInputElement>) => void
}

export type UseCompositionChangeHandler = (ev: ChangeEvent<HTMLInputElement>) => void

export type UseCompositionHandlers<T> = {
    handleCompositionStart: () => void
    handleCompositionEnd: (ev: CompositionEvent<HTMLInputElement>) => void
}

export function useComposition<T>({
    value: propsValue,
    onChange
}: UseCompositionProps<T>): [T | undefined, UseCompositionChangeHandler, UseCompositionHandlers<T>] {
    const [value, setValue] = useState<T | undefined>(propsValue)

    const isComposingRef = useRef(false)

    const handleChange = useCallback(
        (ev: ChangeEvent<HTMLInputElement>) => {
            setValue(ev.target.value as T)
            if (isComposingRef.current) {
                return
            }
            onChange?.(ev)
        },
        [onChange]
    )

    const handleCompositionStart = useCallback(() => {
        isComposingRef.current = true
    }, [])

    const handleCompositionEnd = useCallback(
        (ev: CompositionEvent<HTMLInputElement>) => {
            isComposingRef.current = false
            setValue(ev.currentTarget.value as T)
            onChange?.(ev as unknown as ChangeEvent<HTMLInputElement>)
        },
        [onChange]
    )

    useUpdateEffect(() => {
        if (propsValue !== value) {
            setValue(propsValue)
        }
    }, [propsValue])

    const compositionHandlers = useMemo<UseCompositionHandlers<T>>(
        () => ({
            handleCompositionStart,
            handleCompositionEnd
        }),
        [handleCompositionEnd, handleCompositionStart]
    )

    return [value, handleChange, compositionHandlers]
}
